import * as colors from '../../../../../assets/js/colors'

export const FILTROS_CTE_ORIGEM_INICIAIS = {
  emitentes: [],
  remetentes: [],
  destinatarios: [],
  cidades: [],
}

// 3524 0912 3456 ... (grupos de 4 dígitos, como no DACTE)
export function formatarChaveCte(chave) {
  return String(chave || '').replace(/(\d{4})(?=\d)/g, '$1 ')
}

export function cidadeUf(nome, uf) {
  return `${nome}/${uf}`
}

export function rotaCteOrigem(cte) {
  return `${cidadeUf(cte.municipio_inicio, cte.uf_inicio)} → ${cidadeUf(cte.municipio_fim, cte.uf_fim)}`
}

function opcoesUnicas(valores) {
  return [...new Set(valores.filter(Boolean))]
    .sort((a, b) => a.localeCompare(b))
    .map(valor => ({ label: valor, value: valor }))
}

export function opcoesFiltroCteOrigem(ctes) {
  return {
    emitentes: opcoesUnicas(ctes.map(cte => cte.emitente_nome)),
    remetentes: opcoesUnicas(ctes.map(cte => cte.remetente_nome)),
    destinatarios: opcoesUnicas(ctes.map(cte => cte.destinatario_nome)),
    cidades: opcoesUnicas(
      ctes.reduce(
        (acc, cte) => [
          ...acc,
          cidadeUf(cte.municipio_inicio, cte.uf_inicio),
          cidadeUf(cte.municipio_fim, cte.uf_fim),
        ],
        [],
      ),
    ),
  }
}

// Cidade vale tanto para a origem quanto para o destino da prestação
export function aplicarFiltrosCteOrigem(ctes, filtros) {
  const { emitentes, remetentes, destinatarios, cidades } = filtros
  return ctes.filter(cte => {
    if (emitentes.length && !emitentes.includes(cte.emitente_nome)) return false
    if (remetentes.length && !remetentes.includes(cte.remetente_nome)) return false
    if (destinatarios.length && !destinatarios.includes(cte.destinatario_nome)) return false
    if (
      cidades.length &&
      !cidades.includes(cidadeUf(cte.municipio_inicio, cte.uf_inicio)) &&
      !cidades.includes(cidadeUf(cte.municipio_fim, cte.uf_fim))
    ) {
      return false
    }
    return true
  })
}

function lerArquivo(arquivo) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => resolve({ nome: arquivo.name, xml: reader.result }))
    reader.addEventListener('error', () =>
      reject(new Error(`Não foi possível ler o arquivo "${arquivo.name}"`)),
    )
    reader.readAsText(arquivo)
  })
}

// Um arquivo que não seja .xml barra o lote inteiro antes do envio
export function lerArquivosXml(fileList) {
  const arquivos = Array.from(fileList || [])
  if (!arquivos.length) return Promise.reject(new Error('Nenhum arquivo foi selecionado'))
  const invalidos = arquivos.filter(arquivo => !/\.xml$/i.test(String(arquivo.name || '')))
  if (invalidos.length) {
    return Promise.reject(
      new Error(
        `Selecione apenas arquivos .xml. Arquivo(s) inválido(s): ${invalidos
          .map(arquivo => arquivo.name)
          .join(', ')}`,
      ),
    )
  }
  return Promise.all(arquivos.map(lerArquivo))
}

// "3 CTe(s) importado(s): nº 161, 162, 163"
export function mensagemImportacaoCteOrigem(importados) {
  const numeros = importados.map(cte => cte.numero)
  const lista = numeros.length > 10 ? `${numeros.slice(0, 10).join(', ')}...` : numeros.join(', ')
  return `${importados.length} CTe(s) importado(s): nº ${lista}`
}

// Badge da coluna Emissão: CTe de Subcontratação vigente (autorizado ou em processamento) do CT-e de origem
export function badgeEmissaoCteOrigem(cte) {
  if (cte.cte_status === 'autorizado') return { color: colors.verde, text: 'Emitido' }
  if (cte.cte_status === 'processando_autorizacao') {
    return { color: colors.laranja, text: 'Processando Emissão' }
  }
  return { color: colors.vermelho, text: 'Não Emitido' }
}

const DESCRICAO_STATUS_CTE = {
  autorizado: 'autorizado',
  processando_autorizacao: 'em processamento na SEFAZ',
}

// Só um CTe de Subcontratação por CT-e de origem; tentativas rejeitadas ou canceladas não bloqueiam
export function erroEmissaoCteOrigem(cte) {
  const identificacao = cte.cte_numero ? ` nº ${cte.cte_numero}` : ''
  if (cte.cte_status === 'autorizado') {
    return `Já existe CTe de Subcontratação${identificacao} autorizado para o CTe de origem nº ${cte.numero}`
  }
  if (cte.cte_status === 'processando_autorizacao') {
    return `Já existe CTe de Subcontratação${identificacao} em processamento na SEFAZ para o CTe de origem nº ${cte.numero}. Aguarde a resposta antes de emitir novamente`
  }
  return null
}

// Qualquer CTe gerado, mesmo cancelado ou não autorizado, impede a exclusão do CTe de origem
export function erroExclusaoCteOrigem(cte) {
  if (!(Number(cte.ctes_gerados) > 0)) return null
  const detalhe = DESCRICAO_STATUS_CTE[cte.cte_status]
    ? `nº ${cte.cte_numero} ${DESCRICAO_STATUS_CTE[cte.cte_status]}`
    : 'gerado (cancelado ou não autorizado)'
  return `O CTe de origem nº ${cte.numero} não pode ser excluído: já possui CTe de Subcontratação ${detalhe}`
}

function parseJson(valor, padrao) {
  if (!valor) return padrao
  if (typeof valor !== 'string') return valor
  try {
    return JSON.parse(valor)
  } catch (error) {
    return padrao
  }
}

// "2 NF-e, 1 outro documento" a partir do documentos_json do CT-e de origem
export function descreverDocumentosOrigem(documentosJson) {
  const docs = parseJson(documentosJson, {})
  const partes = []
  const nfes = (docs.nfes || []).length
  const nfs = (docs.nfs || []).length
  const outros = (docs.outros || []).length
  if (nfes) partes.push(`${nfes} NF-e`)
  if (nfs) partes.push(`${nfs} NF em papel`)
  if (outros)
    partes.push(`${outros} outro${outros > 1 ? 's' : ''} documento${outros > 1 ? 's' : ''}`)
  return partes.length ? partes.join(', ') : 'nenhum'
}

// "1.177,98 KG (PESO BRUTO); 12 UN (VOLUMES)" a partir do quantidades_json do CT-e de origem
export function descreverQuantidadesOrigem(quantidadesJson) {
  const unidades = { '00': 'M3', '01': 'KG', '02': 'TON', '03': 'UN', '04': 'L', '05': 'MMBTU' }
  return parseJson(quantidadesJson, [])
    .map(
      q =>
        `${Number(q.quantidade).toLocaleString('pt-BR', { maximumFractionDigits: 4 })} ${
          unidades[q.unidade] || q.unidade
        } (${q.tipo_medida})`,
    )
    .join('; ')
}
